import { useEffect } from 'react'
import {
  Box,
  Typography,
  Avatar,
  AvatarGroup,
  Badge,
} from '@mui/material'
import {
  ChevronRight,
  Forum as ChatIcon,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { chatApi } from '@api/endpoints/chat'
import { useChatStore } from '@store/chatStore'
import { useGeneralChatConnection } from '@hooks/useSignalR'
import { useSessionStore } from '../../stores/sessionStore'

interface GroupChatBannerProps {
  conversationId: string
  onPress?: () => void
}

export default function GroupChatBanner({ conversationId, onPress }: GroupChatBannerProps) {
  const participants = useSessionStore((s) => s.participants)
  const conversations = useChatStore((s) => s.conversations)
  const messagesState = useChatStore((s) => s.messages[conversationId])
  const { setMessages, addMessage } = useChatStore()
  const { isConnected, onNewMessage } = useGeneralChatConnection()

  // Fetch messages for preview
  const { data: messagesData } = useQuery({
    queryKey: ['chatMessages', conversationId],
    queryFn: () => chatApi.getMessages(conversationId, 1, 50),
    enabled: !!conversationId,
  })

  useEffect(() => {
    if (messagesData && conversationId) {
      const messages = Array.isArray(messagesData) ? [...messagesData].reverse() : []
      setMessages(conversationId, messages, 1, false)
    }
  }, [messagesData, conversationId, setMessages])

  // Keep banner preview updated via real-time messages
  useEffect(() => {
    if (!isConnected || !conversationId) return
    const unsub = onNewMessage((message) => {
      if (message.conversationId === conversationId) {
        addMessage(conversationId, message)
      }
    })
    return unsub
  }, [isConnected, conversationId, onNewMessage, addMessage])

  // Derive latest non-system user message
  const latestUserMessage = (() => {
    const items = messagesState?.items
    if (!items?.length) return null
    for (let i = items.length - 1; i >= 0; i--) {
      if (!items[i].isSystemMessage) return items[i]
    }
    return null
  })()

  // Unread count from conversations list
  const conversation = conversations.find((c) => c.id === conversationId)
  const unreadCount = conversation?.unreadMessageCount ?? 0

  return (
    <Box
      onClick={onPress}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        height: 36,
        bgcolor: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.04)'
            : 'rgba(0,0,0,0.03)',
        borderBottom: 1,
        borderColor: 'divider',
        cursor: 'pointer',
        flexShrink: 0,
        '&:hover': {
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.07)'
              : 'rgba(0,0,0,0.05)',
        },
      }}
    >
      {/* Participant avatars */}
      <AvatarGroup
        max={4}
        sx={{
          '& .MuiAvatar-root': {
            width: 22,
            height: 22,
            fontSize: '0.6rem',
            border: '1.5px solid',
            borderColor: 'background.paper',
          },
        }}
      >
        {participants.slice(0, 4).map((p) => (
          <Avatar key={p.userId} src={p.avatarUrl} alt={p.userName}>
            {p.userName?.[0]?.toUpperCase()}
          </Avatar>
        ))}
      </AvatarGroup>

      {/* Latest message preview */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <ChatIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
        {latestUserMessage ? (
          <Typography
            variant="caption"
            noWrap
            sx={{ color: 'text.secondary', lineHeight: 1.3 }}
          >
            <Typography
              component="span"
              variant="caption"
              sx={{ fontWeight: 700, color: 'text.primary' }}
            >
              {latestUserMessage.senderDisplayName || latestUserMessage.senderName}:
            </Typography>
            {' '}
            {latestUserMessage.content}
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Group Chat
          </Typography>
        )}
      </Box>

      {/* Unread badge */}
      {unreadCount > 0 && (
        <Badge
          badgeContent={unreadCount}
          color="primary"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.6rem',
              height: 16,
              minWidth: 16,
            },
          }}
        >
          <Box sx={{ width: 4 }} />
        </Badge>
      )}

      {/* Chevron */}
      <ChevronRight sx={{ fontSize: 18, color: 'text.disabled' }} />
    </Box>
  )
}
